/**
 * Frontend mirror of the backend image-slot walker
 * (`diffit-api/app/utils/image_slots.py::enumerate_image_slots`).
 *
 * Deferred streaming: image slots land as `image_status: 'pending'` and are
 * filled by `image_ready` SSE events, addressed by a stable `image_id`. These
 * helpers find the slots and apply resolved images with the same "guarded,
 * monotonic" rule the backend uses — a slot is only filled while still pending,
 * so a stale project refetch can never downgrade a resolved image back to a
 * skeleton (the A2 race).
 */

import { produce } from 'immer';

/**
 * Yields every image-slot object in a custom-activity or slide-deck structure, in document
 * order. A structure is either sections-shaped (custom activities) or slides-shaped (decks) —
 * never both — so the two branches are independent.
 */
export function walkImageSlots(structure) {
  const slots = [];
  const visit = (section) => {
    if (!section || typeof section !== 'object') return;
    const type = section.type;
    if (type === 'image') {
      slots.push(section);
    } else if (type === 'table') {
      const rows = section.cells || section.prefilled_rows || [];
      for (const row of rows || []) {
        for (const cell of row || []) {
          if (cell && typeof cell === 'object' && cell.type === 'image') slots.push(cell);
        }
      }
    } else if (type === 'matching' && section.matchType === 'image') {
      for (const defn of section.definitions || []) {
        if (defn && typeof defn === 'object') slots.push(defn);
      }
    } else if (type === 'group') {
      for (const child of section.children || []) visit(child);
    }
    // A reading passage's floated image — a first-class slot on (almost always) a
    // text/passage section. Orthogonal to type; mirrors backend enumerate_image_slots.
    if (section.floatImage && typeof section.floatImage === 'object') {
      slots.push(section.floatImage);
    }
  };
  for (const section of structure?.sections || []) visit(section);
  // A slide deck's image slot IS the element's `asset` object — the slide renderer reads
  // `asset.current_image` directly, so the slot must be the asset itself, not a wrapper.
  for (const slide of structure?.slides || []) {
    for (const element of slide?.elements || []) {
      if (element?.asset && typeof element.asset === 'object' && element.asset.type === 'image') {
        slots.push(element.asset);
      }
    }
  }
  return slots;
}

/** The custom-activity structure inside a packet block, or null for non-custom blocks. */
export function structureOfBlock(block) {
  return block?.resource_data?.custom_content?.items?.[0]?.structure || null;
}

/** The guarded patch itself, over an immer draft of one packet's blocks array. */
function applyImageOverlayToBlocksDraft(blocksDraft, overlayMap) {
  for (const block of blocksDraft || []) {
    const structure = structureOfBlock(block);
    if (!structure) continue;
    for (const slot of walkImageSlots(structure)) {
      if (slot.image_status !== 'pending') continue;
      const entry = overlayMap.get(slot.image_id);
      if (!entry) continue;
      if (entry.status === 'ready' && entry.images?.length) {
        slot.current_image = entry.images[0];
        slot.images = entry.images;
        slot.image_status = 'ready';
      } else if (entry.status === 'failed') {
        slot.image_status = 'failed';
      }
    }
  }
}

/**
 * Apply an `image_id -> { status, images }` overlay onto an immer draft of the
 * packets array (`[{ packet: { blocks } }]`). Only still-`pending` slots are
 * touched, so a resolved image is never reverted by a later/ stale snapshot.
 */
export function applyImageOverlayToDraft(packetsDraft, overlayMap) {
  if (!overlayMap || overlayMap.size === 0) return;
  for (const p of packetsDraft || []) {
    if (!Array.isArray(p?.packet?.blocks)) continue;
    applyImageOverlayToBlocksDraft(p.packet.blocks, overlayMap);
  }
}

/**
 * Carry image resolutions from one blocks array onto another, by `image_id`.
 *
 * ProjectPacketContext renders a local copy of the active packet and only
 * re-syncs it wholesale while a create stream is running. Images that resolve
 * after that — the on-load backfill, or a late `image_ready` — land in
 * ProjectContext's packets and would otherwise never reach the screen, leaving
 * the teacher looking at a skeleton whose image is already resolved one context
 * up. This is the narrow bridge: slot-scoped and guarded the same way as the
 * overlay above, so it fills only slots still `pending` in `localBlocks` and
 * cannot touch any other field — a wholesale copy would revert unsaved edits.
 *
 * Returns `localBlocks` unchanged when there is nothing to carry over; callers
 * rely on that reference equality to skip a re-render.
 */
export function syncResolvedImageSlots(localBlocks, sourceBlocks) {
  if (!blocksHavePendingImages(localBlocks)) return localBlocks;
  const overlay = new Map();
  harvestResolvedImageSlots(sourceBlocks, overlay);
  if (!overlay.size) return localBlocks;
  return produce(localBlocks, (draft) => applyImageOverlayToBlocksDraft(draft, overlay));
}

/**
 * Record every already-resolved slot in `blocks` into an overlay map
 * (`image_id -> { status: 'ready'|'failed', images }`), skipping ids the map
 * already has. The backfill endpoint returns the SERVER's view of the blocks;
 * the teacher may have edited while the call was in flight, so callers harvest
 * only the slot resolutions and patch them in via `applyImageOverlayToDraft`
 * instead of replacing the blocks. Returns true if the map gained an entry.
 */
export function harvestResolvedImageSlots(blocks, overlayMap) {
  let harvested = false;
  for (const block of blocks || []) {
    const structure = structureOfBlock(block);
    if (!structure) continue;
    for (const slot of walkImageSlots(structure)) {
      if (!slot.image_id || overlayMap.has(slot.image_id)) continue;
      if (slot.image_status === 'ready' && slot.images?.length) {
        overlayMap.set(slot.image_id, { status: 'ready', images: slot.images });
        harvested = true;
      } else if (slot.image_status === 'failed') {
        overlayMap.set(slot.image_id, { status: 'failed' });
        harvested = true;
      }
    }
  }
  return harvested;
}

/** True if any block in a blocks array still has a pending image slot. */
export function blocksHavePendingImages(blocks) {
  for (const block of blocks || []) {
    const structure = structureOfBlock(block);
    if (!structure) continue;
    for (const slot of walkImageSlots(structure)) {
      if (slot.image_status === 'pending') return true;
    }
  }
  return false;
}

/** True if any packet (`{ packet: { blocks } }`) still has a pending image slot. */
export function hasPendingImages(packets) {
  return (packets || []).some((p) => blocksHavePendingImages(p?.packet?.blocks));
}
